import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProductCard } from '../components/custom/ProductCard';
import { FilterBottomSheet } from '../components/custom/FilterBottomSheet';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { X, Loader2 } from 'lucide-react';
import api from '../utils/api';
import { SEO, SEOPresets } from '../components/SEO';

function ProductListingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const productsPerPage = 20;
  const observerTarget = useRef(null);
  
  const category = searchParams.get('category') || 'all';
  const age = searchParams.get('age') || '';

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`);
        const data = await response.json();
        setCategories(data || []);
      } catch (error) {
        console.error('Failed to fetch categories');
      }
    };
    fetchCategories();
  }, []);

  // SEO titles based on category
  const seoTitles = {
    'all': 'All Kids Lunchboxes & School Bags | Mothering',
    'lunchbox': 'Kids Lunchboxes | Leak-Proof Tiffin Boxes | Mothering',
    'bag': 'Kids School Bags | Backpacks for Children | Mothering'
  };
  
  const seoDescriptions = {
    'all': 'Shop colorful lunchboxes and cool school bags for kids. BPA-free, leak-proof designs. Free delivery across India!',
    'lunchbox': 'Buy leak-proof kids lunchboxes online. Insulated tiffin boxes that keep food warm. Multiple compartments. Free delivery!',
    'bag': 'Shop comfortable school bags for kids. Padded straps, water bottle pockets. Fun designs kids love. Free delivery!'
  };

  // Fetch initial products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setProducts([]);
      setOffset(0);
      setHasMore(true);
      
      try {
        const params = new URLSearchParams();
        if (category !== 'all') {
          params.append('category', category);
        }
        
        params.append('limit', productsPerPage);
        params.append('offset', 0);
        
        // Apply filters
        if (filters.priceRange) {
          params.append('min_price', filters.priceRange[0]);
          params.append('max_price', filters.priceRange[1]);
        }
        if (filters.ageGroups && filters.ageGroups.length > 0) {
          filters.ageGroups.forEach(age => params.append('age_group', age));
        }
        if (filters.features && filters.features.length > 0) {
          filters.features.forEach(feature => params.append('features', feature));
        }
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/products?${params.toString()}`
        );
        const data = await response.json();
        const fetchedProducts = data.products || [];
        setProducts(fetchedProducts);
        setOffset(productsPerPage);
        setHasMore(fetchedProducts.length === productsPerPage);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category, filters]);

  // Load more products
  const loadMoreProducts = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      if (category !== 'all') {
        params.append('category', category);
      }
      
      params.append('limit', productsPerPage);
      params.append('offset', offset);
      
      // Apply filters
      if (filters.priceRange) {
        params.append('min_price', filters.priceRange[0]);
        params.append('max_price', filters.priceRange[1]);
      }
      if (filters.ageGroups && filters.ageGroups.length > 0) {
        filters.ageGroups.forEach(age => params.append('age_group', age));
      }
      if (filters.features && filters.features.length > 0) {
        filters.features.forEach(feature => params.append('features', feature));
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products?${params.toString()}`
      );
      const data = await response.json();
      const fetchedProducts = data.products || [];
      
      setProducts(prev => [...prev, ...fetchedProducts]);
      setOffset(prev => prev + productsPerPage);
      setHasMore(fetchedProducts.length === productsPerPage);
    } catch (error) {
      console.error('Failed to load more products:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [category, filters, offset, loadingMore, hasMore]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMoreProducts();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loadingMore, loading, loadMoreProducts]);

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
  };

  const removeFilter = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: Array.isArray(prev[filterType])
        ? prev[filterType].filter(v => v !== value)
        : filterType === 'priceRange' ? [0, 3000] : prev[filterType]
    }));
  };

  const activeFilterChips = [];
  if (filters.ageGroups) {
    filters.ageGroups.forEach(age => {
      activeFilterChips.push({ type: 'ageGroups', value: age, label: `Age: ${age}` });
    });
  }
  if (filters.features) {
    filters.features.forEach(feat => {
      activeFilterChips.push({ type: 'features', value: feat, label: feat.replace('_', ' ') });
    });
  }

  const handleCategoryChange = (value) => {
    if (value === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: value });
    }
  };

  // Get current category info for SEO
  const currentCategory = categories.find(cat => cat.id === category);
  const categoryName = currentCategory?.name || 'All Products';
  
  // Use SEO presets or customize based on category
  const seoProps = category === 'all' 
    ? SEOPresets.products 
    : {
        ...SEOPresets.categories(categoryName),
        url: `/products?category=${category}`
      };

  return (
    <>
      <SEO {...seoProps} />
      
    <div className="min-h-screen py-3 sm:py-4 px-3 sm:px-4 pb-20 md:pb-8" data-testid="product-listing-page">
      <div className="container mx-auto max-w-6xl">
        <h1 className="text-2xl sm:text-3xl font-heading font-bold mb-3 sm:mb-4">Shop All Products</h1>

        {/* Category Tabs - Dynamic */}
        <div className="mb-3 sm:mb-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => handleCategoryChange('all')}
              className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                category === 'all'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Categories
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                  category === cat.id
                    ? 'bg-cyan-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Filters Bar */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <FilterBottomSheet 
              onApplyFilters={handleApplyFilters}
              activeFilters={filters}
            />
            
            {/* Active Filter Chips */}
            {activeFilterChips.map((chip, index) => (
              <Badge
                key={`${chip.type}-${chip.value}-${index}`}
                variant="secondary"
                className="gap-1"
              >
                {chip.label}
                <X
                  className="w-3 h-3 cursor-pointer"
                  onClick={() => removeFilter(chip.type, chip.value)}
                />
              </Badge>
            ))}
          </div>
          
          <span className="text-sm text-muted-foreground">
            {products.length} products
          </span>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-72 sm:h-80 rounded-xl" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-base sm:text-lg text-muted-foreground">No products found</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
              {products.map((product, idx) => (
                <ProductCard key={product.id} product={product} showBestseller={idx < 2} />
              ))}
            </div>
            
            {/* Infinite scroll trigger & loading indicator */}
            <div ref={observerTarget} className="flex justify-center items-center py-8">
              {loadingMore && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">Loading more products...</span>
                </div>
              )}
              {!hasMore && products.length > 0 && (
                <p className="text-sm text-muted-foreground">You've reached the end!</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );
}

export default ProductListingPage;